import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, User, Loader2, AlertCircle, ChevronRight, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import ReactMarkdown from 'react-markdown';
import { MetallicLogo } from '../components/MetallicLogo';
import { authUser, fetchPitchMetaSWR, fetchSlideHtml } from '../lib/sheets';

const AUTH_SHEET = 'Admin accounts';

// Convert emoji char to Fluent Emoji CDN image
const emojiToFluentUrl = (emoji) => {
  const codepoints = [...emoji]
    .map(c => c.codePointAt(0).toString(16))
    .filter(cp => cp !== 'fe0f') // remove variation selector
    .join('_');
  return `https://cdn.jsdelivr.net/npm/fluentui-emoji@1.3.0/png/${codepoints}.png`;
};

const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;

// Wrap emoji characters with Fluent Emoji images
const AnimatedText = ({ children, type, light }) => {
  if (!children) return null;

  const processChildren = (c) => {
    const processed = [];
    React.Children.forEach(c, (child) => {
      if (typeof child === 'string') {
        const parts = child.split(emojiRegex);
        parts.forEach((part, i) => {
          if (emojiRegex.test(part)) {
            emojiRegex.lastIndex = 0;
            processed.push(
              <img
                key={i}
                src={emojiToFluentUrl(part)}
                alt={part}
                className="inline-block emoji-pop align-middle"
                style={{ width: '1.4em', height: '1.4em', verticalAlign: '-0.2em' }}
                onError={(e) => { e.target.replaceWith(document.createTextNode(part)); }}
              />
            );
          } else if (part) {
            processed.push(part);
          }
        });
      } else {
        processed.push(child);
      }
    });
    return processed;
  };

  const titleBase = `font-benzin leading-tight text-center ${light ? 'text-black' : 'text-white'}`;
  const descBase = `leading-relaxed text-center ${light ? 'text-black/70' : 'text-white/70'}`;

  return (
    <ReactMarkdown
      components={{
        p: ({ children: c }) => (
          <p className={type === 'title'
            ? `${titleBase} text-xl sm:text-2xl lg:text-4xl mb-3`
            : `${descBase} text-sm lg:text-lg mb-3`
          }>{processChildren(c)}</p>
        ),
        h1: ({ children: c }) => <h1 className={`${titleBase} text-2xl sm:text-3xl lg:text-5xl mb-4`}>{processChildren(c)}</h1>,
        h2: ({ children: c }) => <h2 className={`${titleBase} text-xl sm:text-2xl lg:text-4xl mb-3`}>{processChildren(c)}</h2>,
        h3: ({ children: c }) => <h3 className={`${titleBase} text-lg sm:text-xl lg:text-3xl mb-2`}>{processChildren(c)}</h3>,
        strong: ({ children: c }) => <strong className={`font-semibold ${light ? 'text-black' : 'text-white'}`}>{c}</strong>,
        em: ({ children: c }) => <em className={light ? 'text-black/60' : 'text-white/60'}>{c}</em>,
        ul: ({ children: c }) => <ul className="list-disc list-inside space-y-1 mb-3">{c}</ul>,
        ol: ({ children: c }) => <ol className="list-decimal list-inside space-y-1 mb-3">{c}</ol>,
        li: ({ children: c }) => <li>{c}</li>,
      }}
    >
      {children}
    </ReactMarkdown>
  );
};

// Slide transition variants
const slideVariants = {
  enter: (direction) => ({
    opacity: 0,
    y: direction > 0 ? 60 : -60,
  }),
  center: {
    opacity: 1,
    y: 0,
  },
  exit: (direction) => ({
    opacity: 0,
    y: direction > 0 ? -60 : 60,
  }),
};

// Iframe block: bridges keyboard events from iframe to parent, no sandbox
const IframeBlock = ({ html, showOutlines }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const onLoad = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        // Bridge keydown from iframe to parent
        iframeDoc.addEventListener('keydown', (e) => {
          const event = new KeyboardEvent('keydown', {
            key: e.key, code: e.code,
            ctrlKey: e.ctrlKey, shiftKey: e.shiftKey,
            altKey: e.altKey, metaKey: e.metaKey,
            bubbles: true,
          });
          window.dispatchEvent(event);
        });
        // Fix ECharts: dispatch downplay on mouseout from body
        iframeDoc.body.addEventListener('mouseleave', () => {
          const charts = iframe.contentWindow.echarts;
          if (charts) {
            const instances = charts.getInstanceByDom
              ? iframeDoc.querySelectorAll('[_echarts_instance_]')
              : [];
            instances.forEach(el => {
              const inst = charts.getInstanceByDom(el);
              if (inst) inst.dispatchAction({ type: 'downplay' });
            });
          }
        });
      } catch (e) { /* cross-origin error, ignore */ }
    };
    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={`<style>::-webkit-scrollbar{display:none;}</style>${html}`}
      className="absolute rounded-2xl border-0"
      style={{
        zIndex: 0,
        top: '11%',
        bottom: '5%',
        left: 0,
        right: 0,
        width: '100%',
        height: '84%',
        outline: showOutlines ? '2px dashed rgba(0,255,0,0.7)' : 'none',
      }}
      title="Slide content"
    />
  );
};


const Slide = ({ title, description, html, direction, light, showOutlines, bg, isLogoSlide }) => {
  const titleRef = useRef(null);
  const descRef = useRef(null);

  const fitAll = useCallback(() => {
    const fit = (wrapper, canExpand) => {
      if (!wrapper) return;
      const inner = wrapper.firstElementChild;
      if (!inner) return;
      inner.style.transform = '';
      inner.style.transformOrigin = 'top center';

      // If title can expand width first
      if (canExpand) {
        wrapper.style.width = '';
        const wH = wrapper.clientHeight;
        let cH = inner.scrollHeight;
        // Try expanding width in steps before scaling
        const startW = 60;
        const maxW = 90;
        let w = startW;
        while (cH > wH && w < maxW) {
          w += 5;
          wrapper.style.width = `${w}%`;
          cH = inner.scrollHeight;
        }
      }

      const wH = wrapper.clientHeight;
      const cH = inner.scrollHeight;
      if (cH <= 0 || wH <= 0) return;
      if (cH > wH) {
        const scale = Math.max(wH / cH, 0.3);
        const visualH = cH * scale;
        const offset = (wH - visualH) / 2;
        inner.style.transform = `translateY(${offset}px) scale(${scale})`;
      } else {
        const offset = (wH - cH) / 2;
        inner.style.transform = `translateY(${offset}px)`;
      }
    };
    fit(titleRef.current, true);
    fit(descRef.current, false);
  }, []);

  // Run fit on mount and when content changes
  useEffect(() => {
    fitAll();
  }, [title, description, fitAll]);

  return (
    <motion.div
      className="absolute inset-0"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Slide background */}
      <div className={`absolute inset-0 ${bg === 'white' ? 'bg-white' : 'bg-black'}`} />

      {/* Logo slide — slide 0 */}
      {isLogoSlide && (
        <div className="absolute inset-0 flex items-center justify-center">
          <MetallicLogo
            className="w-[450px] h-[212px] lg:w-[600px] lg:h-[283px] 2xl:w-[800px] 2xl:h-[377px]"
            bgColor={bg === 'white' ? '#ffffff' : '#000000'}
          />
        </div>
      )}

      {/* Background: HTML content (Column C) — iframe for script execution */}
      {html && (
        <IframeBlock
          html={html}
          showOutlines={showOutlines}
        />
      )}

      {/* Foreground: Title (Column A) */}
      <div
        ref={titleRef}
        className="absolute left-1/2 px-4 lg:px-8 pointer-events-none"
        style={{ zIndex: 10, top: '2%', height: '9%', width: '60%', maxWidth: 900, transform: 'translateX(-50%)', outline: showOutlines ? '2px dashed rgba(255,0,0,0.7)' : 'none' }}
      >
        <div className="w-full text-center">
          <AnimatedText type="title" light={light}>{title}</AnimatedText>
        </div>
      </div>

      {/* Foreground: Description (Column B) */}
      <div
        ref={descRef}
        className="absolute left-0 right-0 px-4 lg:px-16 pointer-events-none"
        style={{ zIndex: 10, bottom: '2%', height: '25%', outline: showOutlines ? '2px dashed rgba(0,150,255,0.7)' : 'none' }}
      >
        <div className="w-full max-w-4xl mx-auto text-center">
          <AnimatedText type="description" light={light}>{description}</AnimatedText>
        </div>
      </div>
    </motion.div>
  );
};

// Navigation sidebar
const SlideNav = ({ slides, currentSlide, onSelect, navHovered, isLight }) => {
  const [visible, setVisible] = useState(false);
  const hideTimeout = useRef(null);
  const panelRef = useRef(null);

  const show = useCallback(() => {
    clearTimeout(hideTimeout.current);
    setVisible(true);
    navHovered.current = true;
  }, [navHovered]);

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
      navHovered.current = false;
    }, 800);
  }, [navHovered]);

  useEffect(() => {
    const onMove = (e) => {
      // Show when cursor is at the very left edge
      if (e.clientX < 30) {
        show();
      }
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(hideTimeout.current);
    };
  }, [show]);

  return (
    <motion.div
      ref={panelRef}
      className="fixed top-0 left-0 bottom-0 z-40 flex items-center"
      style={{ width: 300 }}
      initial={false}
      animate={{ x: visible ? 0 : -270 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
    >
      <div
        className={`w-[270px] max-h-[80vh] m-4 rounded-2xl border relative ${isLight ? 'border-black/10' : 'border-white/10'}`}
        style={{
          background: isLight ? 'rgba(240, 240, 240, 0.7)' : 'rgba(20, 20, 20, 0.7)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
      >
        {/* Top fade */}
        <div className="absolute top-0 left-0 right-0 h-6 z-10 pointer-events-none rounded-t-2xl"
          style={{ background: isLight ? 'linear-gradient(to bottom, rgba(240,240,240,0.9), transparent)' : 'linear-gradient(to bottom, rgba(20,20,20,0.9), transparent)' }} />
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-6 z-10 pointer-events-none rounded-b-2xl"
          style={{ background: isLight ? 'linear-gradient(to top, rgba(240,240,240,0.9), transparent)' : 'linear-gradient(to top, rgba(20,20,20,0.9), transparent)' }} />

        <div className="overflow-y-auto max-h-[80vh] rounded-2xl px-4 py-5 pitch-nav-scroll" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <div className="space-y-0.5">
            {slides.map((slide, idx) => {
              const level = slide.level || 1;
              const isActive = idx === currentSlide;
              const pl = level === 1 ? 'pl-2' : level === 2 ? 'pl-5' : 'pl-8';

              return (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); onSelect(idx); }}
                  className={`w-full text-left rounded-lg px-3 py-2 ${pl} transition-all duration-200
                    ${isActive
                      ? (isLight ? 'bg-black/10 text-black' : 'bg-white/10 text-white')
                      : (isLight ? 'text-black/40 hover:text-black/70 hover:bg-black/5' : 'text-white/40 hover:text-white/70 hover:bg-white/5')
                    }`}
                  data-testid={`nav-slide-${idx}`}
                >
                  <div className="flex items-center gap-2">
                    {isActive && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                    <span className={`text-sm truncate ${level === 1 ? 'font-medium' : ''}`}>
                      {slide.navTitle || `Слайд ${idx + 1}`}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Right settings panel
const SettingsPanel = ({ lightTheme, onLogout, navHovered }) => {
  const [visible, setVisible] = useState(false);
  const hideTimeout = useRef(null);

  const show = useCallback(() => {
    clearTimeout(hideTimeout.current);
    setVisible(true);
    navHovered.current = true;
  }, [navHovered]);

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
      navHovered.current = false;
    }, 800);
  }, [navHovered]);

  useEffect(() => {
    const onMove = (e) => {
      if (e.clientX > window.innerWidth - 30) {
        show();
      }
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      clearTimeout(hideTimeout.current);
    };
  }, [show]);

  return (
    <motion.div
      className="fixed top-0 right-0 bottom-0 z-40 flex items-center justify-end"
      style={{ width: 220 }}
      initial={false}
      animate={{ x: visible ? 0 : 190 }}
      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
    >
      <div
        className="w-[200px] m-4 rounded-2xl border border-white/10 overflow-hidden"
        style={{
          background: lightTheme ? 'rgba(240, 240, 240, 0.7)' : 'rgba(20, 20, 20, 0.7)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
        }}
      >
        <div className="p-3 space-y-1">
          <button
            onClick={(e) => { e.stopPropagation(); onLogout(); }}
            className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 transition-all duration-200
              ${lightTheme ? 'text-black/60 hover:bg-black/5' : 'text-white/50 hover:text-white/70 hover:bg-white/5'}`}
            data-testid="pitch-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Выйти</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const PitchPresentation = ({ slides, prefetchedHtml, onLogout }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [showOutlines, setShowOutlines] = useState(false);
  const [htmlMap, setHtmlMap] = useState(() => prefetchedHtml || {});
  const fetchingRef = useRef(new Set());
  const transitioning = useRef(false);
  const navHovered = useRef(false);
  const total = slides.length;

  // Sync prefetched HTML, если родитель обновил (старый Apps Script вернул всё сразу)
  useEffect(() => {
    if (prefetchedHtml && Object.keys(prefetchedHtml).length > 0) {
      setHtmlMap((prev) => ({ ...prefetchedHtml, ...prev }));
    }
  }, [prefetchedHtml]);

  // Lazy-fetch HTML для предыдущего, текущего и следующего слайдов
  useEffect(() => {
    const need = [currentSlide - 1, currentSlide, currentSlide + 1];
    need.forEach((idx) => {
      const s = slides[idx];
      if (!s || !s.sheetRow) return;
      if (htmlMap[s.sheetRow] !== undefined) return;
      if (fetchingRef.current.has(s.sheetRow)) return;
      fetchingRef.current.add(s.sheetRow);
      fetchSlideHtml(s.sheetRow).then((html) => {
        setHtmlMap((prev) => ({ ...prev, [s.sheetRow]: html || '' }));
      }).finally(() => {
        fetchingRef.current.delete(s.sheetRow);
      });
    });
  }, [currentSlide, slides, htmlMap]);

  const goTo = useCallback((next, dir) => {
    if (transitioning.current) return;
    if (next < 0 || next >= total) return;
    transitioning.current = true;
    setDirection(dir);
    setCurrentSlide(next);
    setTimeout(() => { transitioning.current = false; }, 500);
  }, [total]);

  const goNext = useCallback(() => goTo(currentSlide + 1, 1), [currentSlide, goTo]);
  const goPrev = useCallback(() => goTo(currentSlide - 1, -1), [currentSlide, goTo]);

  const goToSlide = useCallback((idx) => {
    if (idx === currentSlide) return;
    goTo(idx, idx > currentSlide ? 1 : -1);
  }, [currentSlide, goTo]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      // Backtick (`) — toggle outlines
      if (e.key === '`') {
        e.preventDefault();
        setShowOutlines(p => !p);
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goNext, goPrev]);

  // Scroll / wheel (blocked when nav is hovered)
  useEffect(() => {
    const onWheel = (e) => {
      if (navHovered.current) return;
      if (Math.abs(e.deltaY) < 10) return;
      if (e.deltaY > 0) goNext();
      else goPrev();
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => window.removeEventListener('wheel', onWheel);
  }, [goNext, goPrev]);

  // Right click = prev
  useEffect(() => {
    const onContext = (e) => {
      if (e.target.closest('a, button, input, textarea, select, [data-testid^="nav-slide"]')) return;
      e.preventDefault();
      goPrev();
    };
    window.addEventListener('contextmenu', onContext);
    return () => {
      window.removeEventListener('contextmenu', onContext);
    };
  }, [goPrev]);

  const slide = slides[currentSlide];
  const isLight = slide.bg === 'white';
  const slideHtml = slide.sheetRow ? (htmlMap[slide.sheetRow] || '') : '';

  return (
    <div
      className={`fixed inset-0 overflow-hidden ${isLight ? 'bg-white' : 'bg-black'}`}
      data-testid="pitch-presentation"
    >
      {/* Navigation sidebar */}
      <SlideNav slides={slides} currentSlide={currentSlide} onSelect={goToSlide} navHovered={navHovered} isLight={isLight} />

      {/* Settings panel (right) */}
      <SettingsPanel
        lightTheme={isLight}
        onLogout={onLogout}
        navHovered={navHovered}
      />

      {/* Slide counter */}
      <div className={`fixed bottom-6 right-8 z-30 text-sm font-mono ${isLight ? 'text-black/30' : 'text-white/30'}`}>
        {currentSlide + 1} / {total}
      </div>

      {/* Slide area */}
      <AnimatePresence custom={direction} mode="wait">
        <Slide
          key={currentSlide}
          title={slide.title}
          description={slide.description}
          html={slideHtml}
          direction={direction}
          light={isLight}
          showOutlines={showOutlines}
          bg={slide.bg}
          isLogoSlide={slide.isLogoSlide}
        />
      </AnimatePresence>
    </div>
  );
};

const Pitch = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [slides, setSlides] = useState([]);
  const [loadingSlides, setLoadingSlides] = useState(false);
  const [prefetchedHtml, setPrefetchedHtml] = useState({});

  // Check session — re-verify against the sheet
  useEffect(() => {
    const stored = sessionStorage.getItem('splav_pitch_auth');
    const storedUser = sessionStorage.getItem('splav_pitch_user');
    const storedPass = sessionStorage.getItem('splav_pitch_pass');

    if (stored !== 'true' || !storedUser || !storedPass) return;

    setIsAuthenticated(true);

    authUser(AUTH_SHEET, storedUser, storedPass).then((result) => {
      if (!result.ok) {
        setIsAuthenticated(false);
        sessionStorage.removeItem('splav_pitch_auth');
        sessionStorage.removeItem('splav_pitch_user');
        sessionStorage.removeItem('splav_pitch_pass');
      }
    }).catch(() => {});
  }, []);

  // Load slide metadata after auth (без тяжёлого HTML) — stale-while-revalidate
  useEffect(() => {
    if (!isAuthenticated) return;

    const applyResult = ({ rows, prefilledHtml }) => {
      const parsed = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row) continue;
        const title = row[0] || '';
        const description = row[1] || '';
        const levelRaw = row[3];
        const level = levelRaw ? parseInt(levelRaw, 10) : 1;
        const bgRaw = row[4];
        const bg = (bgRaw && bgRaw.toString().toUpperCase() === 'W') ? 'white' : 'black';
        if (title || description) {
          const navTitle = title.replace(/[#*_~`>\[\]()!]/g, '').trim() || `Слайд ${i}`;
          parsed.push({ title, description, html: '', level, navTitle, bg, sheetRow: i + 1 });
        }
      }
      parsed.unshift({
        title: '', description: '', html: '', level: 1,
        navTitle: 'SPLAV', bg: 'white', isLogoSlide: true,
      });
      setSlides(parsed);
      if (prefilledHtml && Object.keys(prefilledHtml).length > 0) {
        setPrefetchedHtml(prefilledHtml);
      }
    };

    const swr = fetchPitchMetaSWR();
    if (swr.cached && swr.cached.rows && swr.cached.rows.length > 0) {
      applyResult(swr.cached);
      setLoadingSlides(false);
    } else {
      setLoadingSlides(true);
    }
    swr.fresh
      .then((res) => {
        if (res && res.rows && res.rows.length > 0) applyResult(res);
      })
      .catch((err) => console.error('Failed to load pitch:', err))
      .finally(() => setLoadingSlides(false));
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authUser(AUTH_SHEET, username, password);

      if (result.ok) {
        setIsAuthenticated(true);
        sessionStorage.setItem('splav_pitch_auth', 'true');
        sessionStorage.setItem('splav_pitch_user', username);
        sessionStorage.setItem('splav_pitch_pass', password);
      } else {
        setError('Неверное имя пользователя или пароль');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError('Ошибка при проверке данных. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  // Authenticated: show presentation
  if (isAuthenticated) {
    if (loadingSlides) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
        </div>
      );
    }
    if (slides.length === 0) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center">
          <p className="text-white/50">Нет данных в листе PITCH</p>
        </div>
      );
    }
    return <PitchPresentation slides={slides} prefetchedHtml={prefetchedHtml} onLogout={() => {
      setIsAuthenticated(false);
      sessionStorage.removeItem('splav_pitch_auth');
      sessionStorage.removeItem('splav_pitch_user');
      sessionStorage.removeItem('splav_pitch_pass');
    }} />;
  }

  // Login form
  return (
    <div
      data-testid="pitch-auth-page"
      className="min-h-screen bg-black flex flex-col items-center justify-center p-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center justify-center w-12 h-12 bg-white/5 border border-white/10 rounded-xl mb-4"
          >
            <Lock className="w-5 h-5 text-white/70" />
          </motion.div>
          <h1 className="font-benzin text-2xl text-white mb-2">Pitch Deck</h1>
          <p className="text-white/40 text-sm">Авторизация для просмотра</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pitch-user" className="text-white/50 text-sm">Имя пользователя</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                id="pitch-user"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Логин"
                data-testid="pitch-username-input"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 focus:ring-white/20"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="pitch-pass" className="text-white/50 text-sm">Пароль</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input
                id="pitch-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Пароль"
                data-testid="pitch-password-input"
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-white/30 focus:ring-white/20"
                required
              />
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                data-testid="pitch-error-message"
                className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-lg text-white/70 text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            type="submit"
            disabled={isLoading}
            data-testid="pitch-login-btn"
            className="w-full bg-white text-black hover:bg-white/90 font-medium py-5 transition-all duration-300 active:scale-[0.98]"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Проверка...
              </>
            ) : (
              'Войти'
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default Pitch;
